import { FC, useMemo } from 'react';
import { Table, Button, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { RecordGroup } from '#types/System.types';
import { recordGroupColumns } from './recordGroupColumns';
import FlexBox from '../atoms/FlexBox';

const { Text } = Typography;

interface RecordGroupsListProps {
    recordGroups?: { [key: string]: RecordGroup };
    onAdd: () => void;
    onEdit: (groupId: string, group: RecordGroup) => void;
    onDelete: (groupId: string) => void;
}

/**
 * Component to display and manage entity record groups in a table format.
 * Renders a table with infinite scroll showing all record groups with edit/delete actions.
 */
export const RecordGroupsList: FC<RecordGroupsListProps> = ({
    recordGroups,
    onAdd,
    onEdit,
    onDelete
}) => {
    const columns = useMemo(() => recordGroupColumns({
        handleEditGroup: onEdit,
        handleDeleteGroup: onDelete,
    }), [onEdit, onDelete]);

    const dataSource = useMemo(() => 
        Object.entries(recordGroups || {})
            .map(([groupId, group]) => ({
                ...group,
                groupId,
                key: groupId,
            })),
        [recordGroups]
    );

    const renderControls = () => (
        <FlexBox>
            <Text strong>Record Groups</Text>
            <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={onAdd}
                aria-label="Add new record group"
            >
                Add Record Group
            </Button>
        </FlexBox>
    );

    return (
        <Table
            columns={columns}
            dataSource={dataSource}
            title={renderControls}
            footer={dataSource.length > 0 ? renderControls : undefined}
            pagination={false}
            scroll={{ 
                x: 700,
                y: 600
            }}
            size="middle"
            locale={{
                emptyText: 'No record groups defined. Click "Add Record Group" to create one.'
            }}
            aria-label="Record groups table"
            style={{ width: '100%' }}
        />
    );
};